import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { billingAPI, SubscriptionPlan } from '../../services/api';

interface PricingPlansProps {
  currentPlanId?: number;
  onSelectPlan: (plan: SubscriptionPlan) => void;
}

export const PricingPlans: React.FC<PricingPlansProps> = ({
  currentPlanId,
  onSelectPlan,
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await billingAPI.getPlans();
      setPlans(response.data.plans);
    } catch (err) {
      setError(t('billing.errors.loadPlans'));
    } finally {
      setLoading(false);
    }
  };

  const formatLimit = (limit: number | null): string => {
    if (limit === null) return t('billing.unlimited');
    return limit.toString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`px-4 py-3 rounded-xl ${isDark ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-red-50 border border-red-200 text-red-700'}`}>
        {error}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {plans.map((plan) => (
        <div
          key={plan.id}
          className={`relative flex flex-col rounded-xl shadow-lg p-6 border-2 transition-all ${
            currentPlanId === plan.id
              ? 'border-cyan-500'
              : isDark
                ? 'border-slate-700 bg-slate-800 hover:border-slate-600'
                : 'border-transparent bg-white hover:border-gray-200'
          } ${currentPlanId === plan.id && isDark ? 'bg-slate-800' : ''} ${currentPlanId === plan.id && !isDark ? 'bg-white' : ''}`}
        >
          {currentPlanId === plan.id && (
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                {t('billing.currentPlan')}
              </span>
            </div>
          )}

          <div className="text-center mb-6">
            <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h3>
            {plan.description && (
              <p className={`mt-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{plan.description}</p>
            )}
          </div>

          <div className="text-center mb-6">
            <span className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              ${plan.price.toFixed(2)}
            </span>
            <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>
              /{t(`billing.interval.${plan.billing_interval}`)}
            </span>
            <span className={`block text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              {t('billing.perUser')}
            </span>
          </div>

          <ul className="space-y-3 flex-grow">
            <li className={`flex items-center text-sm ${isDark ? 'text-slate-300' : ''}`}>
              <svg
                className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span>
                {formatLimit(plan.user_limit)} {t('billing.limits.users')}
              </span>
            </li>
            <li className={`flex items-center text-sm ${isDark ? 'text-slate-300' : ''}`}>
              <svg
                className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span>
                {formatLimit(plan.time_entry_limit)}{' '}
                {t('billing.limits.timeEntries')}
              </span>
            </li>
            <li className={`flex items-center text-sm ${isDark ? 'text-slate-300' : ''}`}>
              <svg
                className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span>
                {formatLimit(plan.ai_analysis_limit)}{' '}
                {t('billing.limits.aiAnalysis')}
              </span>
            </li>
            {plan.features.map((feature, index) => (
              <li key={index} className={`flex items-center text-sm ${isDark ? 'text-slate-300' : ''}`}>
                <svg
                  className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          <button
            onClick={() => onSelectPlan(plan)}
            disabled={currentPlanId === plan.id}
            className={`w-full py-2 px-4 rounded-lg font-semibold transition-all mt-6 ${
              currentPlanId === plan.id
                ? isDark ? 'bg-slate-700 text-slate-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-400 hover:to-blue-500'
            }`}
          >
            {currentPlanId === plan.id
              ? t('billing.currentPlan')
              : t('billing.selectPlan')}
          </button>
        </div>
      ))}
    </div>
  );
};

export default PricingPlans;
